import { Injectable, inject } from '@angular/core';
import { AuthService } from '../../features/auth/services/auth.service';
import { InstanceConfigService } from '../../core/services/instance-config.service';
import { SolicitudesPendientesService } from '../../features/servicio-tecnico/services/solicitudes-pendientes.service';
import { SolicitudesWebPendientesService } from '../../features/servicio-tecnico/services/solicitudes-web-pendientes.service';
import { OrdenesEnRecepcionService } from '../../features/servicio-tecnico/services/ordenes-en-recepcion.service';
import { CotizacionesPorValorarService } from '../../features/cotizaciones/services/cotizaciones-por-valorar.service';
import {
  LucideIconData, LayoutDashboard, Users, Building2, FileText, FilePen,
  Store, ShoppingCart, Package, Box, Layers, Tags, FileSpreadsheet, ShoppingBag,
  IdCard, UserRound, Car, Truck, List, ArrowUpRight, ArrowDownLeft, Banknote,
  Wallet, FileCheck, FileOutput, Clock, BookOpen, Settings,
  Inbox, Globe, Wrench, ReceiptText, TrendingUp, CirclePlus, TriangleAlert,
  ClipboardList, CalendarCheck, Calculator,
} from 'lucide-angular';
import { PanelResumenService } from '../../features/admin/services/panel-resumen.service';

/* ============================================================
 * Nuevo modelo (v16) para sr-sidebar — íconos Lucide + counts.
 * ============================================================ */
export type SrSidebarSeverity = 'default' | 'danger' | 'warning';

export interface SrSidebarMenuItem {
  label: string;
  icon: LucideIconData;
  route?: string;
  queryParams?: Record<string, string>;
  /** Badge contador (a la derecha). undefined = sin badge. */
  count?: number;
  /** Color del badge: default (neutral) | danger | warning. */
  severity?: SrSidebarSeverity;
  /** Hijos colapsables (sub-items). */
  children?: SrSidebarMenuItem[];
  /** Inicialmente expandido cuando tiene children. */
  expanded?: boolean;
  /** routerLinkActiveOptions.exact (true para /dashboard). */
  exact?: boolean;
}

export interface SrSidebarMenuSection {
  label?: string;
  items: SrSidebarMenuItem[];
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  private instanceConfig = inject(InstanceConfigService);
  private solicitudesPendientes = inject(SolicitudesPendientesService);
  private solicitudesWebPendientes = inject(SolicitudesWebPendientesService);
  private ordenesEnRecepcion = inject(OrdenesEnRecepcionService);
  private cotizacionesPorValorar = inject(CotizacionesPorValorarService);
  private panelResumen = inject(PanelResumenService);

  constructor(private authService: AuthService) {}

  /* ============================================================
   * sr-sidebar v16 · misma estructura/permisos que el sidebar
   * pero con íconos Lucide + counts/severity (#380).
   * Los counts siguen hardcodeados salvo el de Solicitudes, que sale de
   * `SolicitudesPendientesService` (#767).
   * ============================================================ */
  buildSrSidebarMenu(): SrSidebarMenuSection[] {
    const isAdmin = this.authService.isAdmin();
    const canManageEquipment = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'
    ]);
    const canManageBilling = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'
    ]);
    const canManageFinances = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'
    ]);
    const canManageContracts = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'
    ]);
    // Modo instancia (#553): en COSTOS se ocultan guías y emisión DTE.
    // Se lee dentro del build para que los computed que lo envuelven
    // (layout/bottom-nav) re-construyan el menú cuando llegue la config.
    const emiteDte = this.instanceConfig.emiteDte();
    // Usa guías de despacho/retiro. Depende del MODO de instancia, no de si
    // emite documentos tributarios por ellas.
    const usaGuias = this.instanceConfig.requiereGuiaParaActivar();
    // Una cuenta solo de taller no arrienda (23-oct-2026): sin obras, contratos, guías, estados de pago ni flota propia.
    // Lo comparte con el taller —clientes, cotizaciones, productos, facturación, compras y personal— sigue.
    const arrienda = this.instanceConfig.muestraArriendo();

    const sections: SrSidebarMenuSection[] = [];

    // ── CUENTA DE TALLER (mecánico) ──
    // Un TECNICO no gestiona nada del negocio —ni clientes, ni contratos, ni
    // facturación—: recibe y entrega máquinas, y ve el equipo. Las secciones
    // de abajo se arman con flags por ROL (`canManageEquipment` y compañía
    // incluyen `ROLE_OPERATOR` entero, sin distinguir chofer de mecánico), así
    // que sin este corte temprano vería el menú de gestión completo apenas
    // entrara al shell por /servicio-tecnico (#853).
    //
    // Solo «Órdenes de trabajo»: las solicitudes las gestiona administración
    // (aceptar, rechazar, abrir la orden) y el técnico recibe el trabajo ya
    // convertido en orden.
    // El supervisor de servicio (#860) ve lo mismo: revisa órdenes, no
    // gestiona el negocio.
    if (this.authService.esDelTaller()) {
      return [
        this.seccionServicioTecnico(false),
        // La ficha de un equipo de la flota propia: sin arriendo no hay flota.
        ...(arrienda ? [{ items: [{ label: 'Equipos y maquinarias', icon: Box, route: '/equipos' }] }] : []),
      ];
    }

    // ── CUENTA DE PLATAFORMA ──
    // Nuestra propia cuenta no arrienda nada: su base no tiene equipos,
    // contratos ni clientes. Mostrar el sistema completo vacío hace parecer que
    // está roto, así que acá el menú es solo la administración.
    //
    // Dos grupos, como el mockup aprobado el 29-oct: las cuentas —lo que se
    // opera a diario— y lo comercial, que se toca de vez en cuando. Las
    // funcionalidades de cada cliente viven en su ficha, no en una entrada
    // aparte. Los contadores son colas de acción, como los del taller: altas
    // que quedaron a medias y cuotas del mes que vencieron sin pagarse.
    if (this.instanceConfig.esPanelPlataforma()) {
      const altas = this.panelResumen.altasPendientes().length;
      const atrasados = this.panelResumen.cobrosAtrasados();
      sections.push({
        label: 'Cuentas',
        items: [
          { label: 'Clientes', icon: Building2, route: '/admin/plataforma' },
          { label: 'Alta de cliente', icon: CirclePlus, route: '/admin/alta-cliente',
            count: altas > 0 ? altas : undefined },
          { label: 'Cobranza', icon: ReceiptText, route: '/admin/pagos-suscripcion',
            count: atrasados > 0 ? atrasados : undefined, severity: 'danger' },
        ],
      });
      sections.push(this.seccionComercialDelPanel());
      return sections;
    }

    // ── INICIO ──
    // En COSTOS el home es Rentabilidad (Michael casi no factura: el dashboard
    // de ATM no le sirve). El guard de /dashboard refuerza el redirect, y la
    // Rentabilidad NO se repite abajo en "Costos". En COMPLETO, dashboard normal.
    // Sin arriendo, el inicio son las órdenes de trabajo, que ya están en su sección: un «Inicio» que lleva ahí marcaría
    // dos ítems activos a la vez.
    const homeCostos = this.instanceConfig.muestraRentabilidad();
    if (!this.instanceConfig.soloServicioTecnico()) {
      sections.push({
        items: [
          homeCostos
            ? { label: 'Rentabilidad', icon: TrendingUp, route: '/rentabilidad', exact: true }
            : { label: 'Inicio', icon: LayoutDashboard, route: '/dashboard', exact: true }
        ]
      });
    }

    // ── GESTIÓN ──
    const gestionItems: SrSidebarMenuItem[] = [];
    if (canManageBilling) {
      gestionItems.push({ label: 'Clientes', icon: Users, route: '/clientes' });
    }
    if (canManageEquipment && arrienda) {
      gestionItems.push({ label: 'Obras', icon: Building2, route: '/obras' });
    }
    if (canManageContracts && arrienda) {
      gestionItems.push({ label: 'Contratos', icon: FileText, route: '/contratos' });
    }
    if (canManageFinances) {
      const porValorar = this.cotizacionesPorValorar.cantidad();
      gestionItems.push({ label: 'Cotizaciones', icon: FilePen, route: '/cotizaciones',
        count: porValorar > 0 ? porValorar : undefined, severity: 'warning' });
    }
    if (gestionItems.length) {
      sections.push({ label: 'Gestión', items: gestionItems });
    }

    // ── COMPRAS (nueva sección extraída de Gestión) ──
    const comprasItems: SrSidebarMenuItem[] = [];
    if (canManageBilling) {
      comprasItems.push({ label: 'Proveedores', icon: Store, route: '/proveedores' });
      comprasItems.push({ label: 'Órdenes de compra', icon: ShoppingCart, route: '/ordenes-compra', severity: 'warning' });
    }
    if (comprasItems.length) {
      sections.push({ label: 'Compras', items: comprasItems });
    }

    // ── RECURSOS ──
    const recursosItems: SrSidebarMenuItem[] = [];
    if (canManageEquipment) {
      recursosItems.push({
        label: 'Inventario', icon: Package, children: [
          // La flota que se arrienda, con su catálogo y sus precios.
          ...(arrienda ? [
            { label: 'Equipos y maquinarias', icon: Box, route: '/equipos' },
            { label: 'Catálogo', icon: Layers, route: '/equipos/catalogo' },
            { label: 'Categorías', icon: Tags, route: '/equipos/categorias' },
            { label: 'Listado de Precios', icon: FileSpreadsheet, route: '/equipos/listado-precios' },
          ] : []),
          { label: 'Productos', icon: ShoppingBag, route: '/productos' },
          // El catálogo de servicios (mano de obra, traslados, mantenciones): gestión y tenants con servicio técnico.
          ...(this.instanceConfig.muestraServicioTecnico()
              && this.authService.hasAnyRole(['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'])
            ? [{ label: 'Servicios', icon: Wrench, route: '/servicios' }] : []),
        ]
      });
    }
    if (canManageContracts) {
      recursosItems.push({
        label: 'Flota y Personal', icon: IdCard, children: [
          { label: 'Trabajadores', icon: UserRound, route: '/trabajadores' },
          { label: 'Vehículos', icon: Car, route: '/flota' },
        ]
      });
    }
    if (recursosItems.length) {
      sections.push({ label: 'Recursos', items: recursosItems });
    }

    // ── COSTOS (modo COSTOS, epic Haiken #555/#559) ──
    // Clasificación + gastos. Rentabilidad NO va aquí: es el "Inicio" (arriba),
    // para no duplicar la misma ruta y no dejar dos ítems activos a la vez.
    if (this.instanceConfig.muestraModuloGastos() && canManageFinances) {
      const costosItems: SrSidebarMenuItem[] = [
        { label: 'Clasificar gastos', icon: Inbox, route: '/gastos/pendientes' },
        { label: 'Gastos por máquina', icon: ReceiptText, route: '/gastos' },
      ];
      sections.push({ label: 'Costos', items: costosItems });
    }

    // ── SERVICIO TÉCNICO ──
    // Sección propia y no un ítem más de Operaciones: es un dominio entero
    // (solicitudes, órdenes, expediente) y mezclado con guías y despachos se
    // perdía. Lo de adentro de la orden —checklist, diagnóstico, firmas,
    // documentos— vive en la orden, no en el menú (#851).
    //
    // El taller lo opera terreno, no finanzas: por eso no va detrás de
    // `canManageFinances`.
    if (this.instanceConfig.muestraServicioTecnico() && canManageEquipment) {
      sections.push(this.seccionServicioTecnico(true));
    }

    // ── FINANZAS ──
    const finanzasItems: SrSidebarMenuItem[] = [];
    if (canManageContracts && arrienda) {
      // En COSTOS no existen guías: el arriendo se activa/finaliza/traslada
      // directo desde el detalle del contrato (#552).
      //
      // El criterio es "requiere guía para activar" y NO "emite DTE": son cosas
      // distintas y confundirlas deja sin operar a quien usa guías sin
      // emitirlas. Comulsa despacha y retira equipos como cualquiera —la guía
      // es lo que mueve el equipo y lo que alimenta los días cobrables del
      // estado de pago—, solo que factura por SAP y no emite el DTE 52.
      const operacionesChildren: SrSidebarMenuItem[] = usaGuias ? [
        { label: 'Guías', icon: List, route: '/facturacion/guias' },
        { label: 'Despachar Equipos', icon: ArrowUpRight, route: '/facturacion/guias/despacho' },
        { label: 'Retirar Equipos', icon: ArrowDownLeft, route: '/facturacion/guias/retiro' },
      ] : [];
      if (canManageFinances && this.instanceConfig.muestraHorometro()) {
        operacionesChildren.push({ label: 'Jornadas / Horómetro', icon: Clock, route: '/jornadas' });
      }
      // Deterioros va en Operaciones y no en Equipos: lo que se hace acá es
      // decidir un cobro, y quien lo hace viene del mismo lado que arma el
      // estado de pago.
      if (canManageFinances && this.instanceConfig.muestraDeterioros()) {
        operacionesChildren.push({ label: 'Deterioros', icon: TriangleAlert, route: '/deterioros' });
      }
      if (operacionesChildren.length) {
        finanzasItems.push({
          label: 'Operaciones', icon: Truck, children: operacionesChildren,
        });
      }
    }
    if (canManageBilling) {
      // Estados de pago se contratan: una cuenta solo de taller no los usa.
      const facturacionChildren: SrSidebarMenuItem[] = arrienda && this.instanceConfig.muestraEstadosPago()
        ? [{ label: 'Estados de Pago', icon: Wallet, route: '/facturacion/estados-pago' }]
        : [];
      if (emiteDte) {
        facturacionChildren.push(
          { label: 'Documentos Tributarios', icon: FileCheck, route: '/facturacion/documentos' },
          { label: 'Emitir Documento', icon: FileOutput, route: '/facturacion/documentos/emitir' },
          { label: 'Cuentas por Cobrar', icon: Clock, route: '/facturacion/reportes/por-cobrar' },
        );
        // Libros SII (contabilidad): sin OPERATOR. La visibilidad calza con el
        // @PreAuthorize del backend (OWNER/ADMIN/SUPER_ADMIN).
        if (canManageFinances) {
          facturacionChildren.push(
            { label: 'Libro de Ventas', icon: BookOpen, route: '/facturacion/reportes/libro-ventas' },
            { label: 'Libro de Compras', icon: ReceiptText, route: '/facturacion/compras' },
          );
        }
      }
      if (facturacionChildren.length) {
        finanzasItems.push({
          label: 'Facturación', icon: Banknote, children: facturacionChildren,
        });
      }
    }
    if (finanzasItems.length) {
      sections.push({ label: 'Finanzas', items: finanzasItems });
    }

    // ── SISTEMA ──
    // Configuración como item plano · sin children. El submenú interno
    // (Empresa, Usuarios, SII, etc.) vive en el shell de /configuracion
    // — feedback usuario: "el submenú lateral genera ruido visual".
    const sistemaItems: SrSidebarMenuItem[] = [];
    if (isAdmin) {
      sistemaItems.push({
        label: 'Configuración', icon: Settings, route: '/configuracion',
      });
    }
    // La administración de la plataforma ya no vive dentro de la cuenta de cada
    // cliente: ahí mostraba las empresas de ESA base bajo el rótulo "vista
    // global del SaaS", que era engañoso. Ahora está en admin.softrent.cl, con
    // su propio menú (ver el bloque de PANEL_PLATAFORMA arriba).

    if (sistemaItems.length) {
      sections.push({ label: 'Sistema', items: sistemaItems });
    }

    return sections;
  }

  /**
   * La sección «Servicio Técnico»: el título es el grupo, no una pantalla.
   *
   * <p>Los contadores son colas de acción, nunca el total de registros —un
   * «0» o un «37» permanentes dejan de leerse a los dos días—, y solo se
   * muestran si hay algo:
   * <ul>
   *   <li><b>Solicitudes</b>: las que nadie revisó. Es la única forma en que
   *       el taller se entera de que llegó una: no hay correo, a propósito
   *       (#767).</li>
   *   <li><b>Órdenes de trabajo</b>: depende de quién mira. Administración
   *       ve las que el taller dio por listas y esperan su revisión; el
   *       taller, las máquinas que llegaron y nadie recibió. Una máquina en
   *       reparación de tres días no espera a nadie y no se cuenta.</li>
   * </ul>
   *
   * @param conSolicitudes el técnico no gestiona solicitudes: recibe el
   *        trabajo ya convertido en orden.
   */
  private seccionServicioTecnico(conSolicitudes: boolean): SrSidebarMenuSection {
    const items: SrSidebarMenuItem[] = [];

    if (conSolicitudes) {
      const sinRevisar = this.solicitudesPendientes.cantidad();
      items.push({
        label: 'Solicitudes', icon: Inbox, route: '/solicitudes-servicio',
        count: sinRevisar > 0 ? sinRevisar : undefined,
        severity: 'warning',
      });

      // Los leads de la landing, solo para quien los puede abrir y solo si este tenant tiene el
      // canal: sin eso la entrada sería una bandeja que nunca recibe nada.
      if (this.solicitudesWebPendientes.habilitado()
        && this.authService.hasAnyRole(['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'])) {
        const nuevas = this.solicitudesWebPendientes.cantidad();
        items.push({
          label: 'Solicitudes web', icon: Globe, route: '/solicitudes-web',
          count: nuevas > 0 ? nuevas : undefined,
          severity: 'warning',
        });
      }
    }

    // La cola es lo que le toca a quien mira: administración y el supervisor
    // revisan lo que el técnico cerró; el taller recibe máquinas.
    const cola = this.authService.isAdmin() || this.authService.isSupervisorServicio()
      ? this.ordenesEnRecepcion.porRevisar()
      : this.ordenesEnRecepcion.cantidad();
    // La portada del mecánico (ST-20): lo suyo de hoy. Solo para quien trabaja las máquinas.
    if (this.authService.isTecnico()) {
      items.push({ label: 'Mi jornada', icon: CalendarCheck, route: '/servicio-tecnico/jornada' });
    }
    items.push({
      label: 'Órdenes de trabajo', icon: ClipboardList, route: '/servicio-tecnico',
      count: cola > 0 ? cola : undefined,
      severity: 'warning',
    });

    return { label: 'Servicio Técnico', items };
  }

  /**
   * Lo comercial del panel de plataforma: Planes y el Cotizador. Cotizador va
   * pegado a Planes porque de ahí saca los precios: el orden dice dónde se
   * cambia un valor y dónde solo se usa. Lo comparten el menú lateral y el
   * «Más» de la barra inferior.
   */
  seccionComercialDelPanel(): SrSidebarMenuSection {
    return {
      label: 'Comercial',
      items: [
        { label: 'Planes', icon: Layers, route: '/admin/planes' },
        { label: 'Cotizador', icon: Calculator, route: '/admin/cotizador' },
      ],
    };
  }

  /**
   * v16 · Items aplanados para el drawer "Más" del bottom-nav, con íconos Lucide.
   * Reutiliza buildSrSidebarMenu() como fuente única (misma estructura/permisos que
   * el sidebar desktop) para no duplicar la definición del menú.
   */
  buildMobileMenuItemsV16(): Array<{ label: string; icon: LucideIconData; route: string }> {
    const sections = this.buildSrSidebarMenu();
    const items: Array<{ label: string; icon: LucideIconData; route: string }> = [];

    sections.forEach(section => {
      section.items.forEach(item => {
        if (item.route) {
          items.push({ label: item.label, icon: item.icon, route: item.route });
        } else if (item.children) {
          item.children.forEach(child => {
            if (child.route) {
              items.push({ label: child.label, icon: child.icon, route: child.route });
            }
          });
        }
      });
    });

    items.push({ label: 'Mi Perfil', icon: UserRound, route: '/perfil' });

    return items;
  }

  /**
   * v16 · Acciones de creación rápida del CTA central "+" del bottom-nav.
   * Gateadas por rol (igual criterio que el sidebar). Las guías se crean vía
   * despacho, por eso "Despachar equipos" es el equivalente a "Nueva guía".
   *
   * <p>No reutiliza `buildSrSidebarMenu()` —es una lista plana con su propio
   * shape—, así que el corte de TECNICO va aparte: sin esto, un mecánico en
   * el celular veía "Nuevo contrato"/"Nuevo cliente" en el "+" central,
   * las mismas acciones de gestión que ya se sacaron del sidebar (#853).
   */
  buildQuickCreateItems(): Array<{ label: string; icon: LucideIconData; route: string }> {
    if (this.authService.esDelTaller()) return [];
    // La cuenta de plataforma no despacha ni cotiza: su base no tiene equipos
    // ni clientes, y el «+» ofrecía «Despachar equipos» y «Nuevo contrato».
    if (this.instanceConfig.esPanelPlataforma()) return [];

    const canManageBilling = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'
    ]);
    const canManageFinances = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'
    ]);
    const canManageContracts = this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR'
    ]);

    const items: Array<{ label: string; icon: LucideIconData; route: string }> = [];
    // En COSTOS no hay guías: el despacho rápido no aplica (#553). El criterio
    // es si la instancia USA guías, no si emite DTE por ellas.
    const arrienda = this.instanceConfig.muestraArriendo();
    if (canManageContracts && arrienda && this.instanceConfig.requiereGuiaParaActivar()) {
      items.push({ label: 'Despachar equipos', icon: ArrowUpRight, route: '/facturacion/guias/despacho' });
    }
    // Registro rápido de gastos (#555) — el flujo telefónico de Michael.
    if (this.instanceConfig.muestraModuloGastos() && this.authService.hasAnyRole([
      'ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'
    ])) {
      items.push({ label: 'Registrar gasto', icon: ReceiptText, route: '/gastos/nuevo' });
    }
    if (canManageFinances) {
      items.push({ label: 'Nueva cotización', icon: FilePen, route: '/cotizaciones/nueva' });
    }
    if (canManageContracts && arrienda) {
      items.push({ label: 'Nuevo contrato', icon: FileText, route: '/contratos/nuevo' });
    }
    if (canManageBilling) {
      items.push({ label: 'Nuevo cliente', icon: Users, route: '/clientes/nuevo' });
    }
    return items;
  }
}
